import type { Features, Locale } from '@/hooks/featureFlagHooks';
import { getLocaleData } from '@/selectors/localeSelectors';
import {
  getCenturionNetworkFlags,
  getCulinaryCollectiveFlags,
} from '@/selectors/configSelectors';
import { supportedMarkets } from '@/constants/supportedLocales';

export const convertStringFlagToList = <T extends string = string>(
  featureFlagsString: T
): T[] => {
  if (!featureFlagsString) {
    return [] as T[];
  }

  return featureFlagsString
    .split(',')
    .map((flag) => flag.trim())
    .filter(Boolean)
    .map((flag) => flag as T);
};

export const canViewTCL = ({
  state,
  feature,
}: {
  state: object;
  feature: Features;
}) => {
  const locale: Locale = getLocaleData(state).locale.toLowerCase();
  const centurionNetworkFlags = getCenturionNetworkFlags(state);
  const culinaryCollectiveFlags = getCulinaryCollectiveFlags(state);

  const featureFlags = {
    CULINARY_COLLECTIVE_PAGE: new Set<Locale>(
      convertStringFlagToList<Locale>(culinaryCollectiveFlags)
    ),
    CENTURION_NETWORK_PAGE: new Set<Locale>(
      convertStringFlagToList<Locale>(centurionNetworkFlags)
    ),
  };

  return featureFlags[feature].has(locale);
};

export const parseEnabledMarkets = (
  enabledMarketsString: string
): Set<string> => {
  if (!enabledMarketsString) {
    return new Set<string>();
  }

  return new Set(
    enabledMarketsString
      .split(',')
      .map((pair) => pair.trim())
      .filter(Boolean)
  );
};

export const getFilteredSupportedMarkets = (enabledMarketsConfig: string) => {
  const enabledSet = parseEnabledMarkets(enabledMarketsConfig);

  if (enabledSet.size === 0) {
    return supportedMarkets;
  }

  return supportedMarkets
    .map((market) => ({
      ...market,
      products: market.products.filter((product) =>
        enabledSet.has(`${market.locale}:${product.name}`)
      ),
    }))
    .filter((market) => market.products.length > 0);
};
